import yaml from "js-yaml";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createAssignment,
  terminateAssignment,
} from "../../services/assignmentService";
import debuggerService from "../../services/debuggerService";
import { useToast } from "../common/ToastContext";

const BUFFER_SIZE = 10000;

const DebuggerStateManager = () => {
  const [output, setOutput] = useState([]);
  const [isDebugging, setIsDebugging] = useState(false);
  const [isOpenAssignment, setIsOpenAssignment] = useState(false);
  const [processedCallbacks, setProcessedCallbacks] = useState(new Set());
  const removeListenerRef = useRef(null);
  const isDebuggingRef = useRef(false);
  const [assignmentId, setAssignmentId] = useState(null);
  const { showToast } = useToast();

  const append = useCallback((text, newLines = 0) => {
    newLines++;
    setOutput((prev) => {
      let newOutput;
      if (typeof text === "object") {
        newOutput = [
          ...prev,
          { type: "json", content: JSON.stringify(text, null, 2) },
        ];
      } else {
        newOutput = [...prev, { type: "text", content: text }];
      }
      if (newLines > 0) {
        newOutput.push({ type: "text", content: "\n".repeat(newLines) });
      }
      return newOutput.slice(-BUFFER_SIZE);
    });
  }, []);

  const startDebugging = useCallback(
    async (launchConfig) => {
      if (isDebuggingRef.current) {
        append("Debugging session already in progress (use 'halt' to end)");
        return;
      }
      isDebuggingRef.current = true;
      setIsDebugging(true);
      try {
        await debuggerService.connect();
        removeListenerRef.current = debuggerService.onMessage((msg) => {
          handleDebugMessage(msg, launchConfig);
        });
      } catch (error) {
        append(`Failed to connect to debugger: ${error.message}`);
        isDebuggingRef.current = false;
        setIsDebugging(false);
      }
    },
    [append],
  );

  const handleDebugMessage = useCallback(
    (message, launchConfig) => {
      try {
        // if the message contains a payload, and its of type string, attempt to parse it as JSON
        if (message.payload && typeof message.payload === "string") {
          try {
            message.payload = JSON.parse(message.payload);
          } catch (error) {
            // if the payload is not valid JSON, ignore the error and let the payload alone, continue processing
          }
        }
        append(message);
        if (message.url && message.url.includes("/debugger/cb")) {
          const callbackId = message.url;
          if (!processedCallbacks.has(callbackId)) {
            setProcessedCallbacks((prev) => new Set(prev).add(callbackId));
            sendAssignment(message, launchConfig);
          }
        }
        if (message.event === "assignment_closed") {
          endDebugging();
        }
        // Parse and dispatch the message to MessageBubbleView
        if (message.assignment_id) {
          if (!assignmentId) {
            // fallback to set assignment id only if not already set
            setAssignmentId(message.assignment_id);
          }
          window.dispatchEvent(
            new CustomEvent("eventStreamUpdate", { detail: message }),
          );
        }
      } catch (error) {
        console.error("Error handling debug message:", error);
        append(message);
      }
    },
    [append, processedCallbacks],
  );

  const stringifyValues = useCallback((obj) => {
    if (typeof obj !== "object" || obj === null) {
      return (obj ?? "").toString();
    }
    const stringifiedObj = Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        let stringifiedValue;
        if (
          typeof value === "object" &&
          !(value instanceof Date) &&
          value !== null
        ) {
          stringifiedValue = stringifyValues(value);
        } else {
          stringifiedValue =
            value === null || value === undefined ? "" : String(value);
        }
        return [key, stringifiedValue];
      }),
    );
    return stringifiedObj;
  }, []);

  const sendAssignment = useCallback(
    async (callback, launchConfig) => {
      if (!launchConfig) {
        append("Error: No launch configuration available. Debug aborted.");
        return;
      }
      let yamlContent = launchConfig.content;
      try {
        const parsedYaml = stringifyValues(yaml.load(yamlContent));
        const persona_id = parsedYaml.persona_id;
        const modality = parsedYaml.modality;
        let variables = parsedYaml.variables || {};
        variables["modality"] = modality;
        let recipient_phone_number = parsedYaml.recipient_phone_number || null;
        let links = parsedYaml.links || null;
        let text_inactivity_timeout_minutes =
          parsedYaml.text_inactivity_timeout_minutes || null;
        if (!persona_id) {
          append(
            "Error: Persona is not defined in launch configuration. Debug aborted.",
          );
          return;
        }
        if (recipient_phone_number && !recipient_phone_number.includes("ww")) {
          recipient_phone_number = `${recipient_phone_number}`.replace(
            /[^0-9]/g,
            "",
          );
          if (recipient_phone_number.length === 10) {
            recipient_phone_number = `1${recipient_phone_number}`;
          }
          if (recipient_phone_number.length === 11) {
            recipient_phone_number = `+${recipient_phone_number}`;
          }
          if (!recipient_phone_number.match(/^\+\d{11}$/)) {
            append("Error: Invalid phone number format. Debug aborted.");
            return;
          }
        }
        const request = {
          persona_id: persona_id,
          recipient_phone_number: recipient_phone_number,
          variables: variables,
          callback: callback,
          topic: parsedYaml.topic,
          links: links,
          text_inactivity_timeout_minutes: text_inactivity_timeout_minutes,
        };
        append({ type: "Assignment request", content: request });

        try {
          const response = await createAssignment(request);

          if (response.error) {
            append(`❌ ${response.error}`);
            showToast(response.error, "error");
            endDebugging();
            append("Halt Debugging");
            return;
          }

          if (response.data.id) {
            setAssignmentId(response.data.id);
          }

          // Success case
          append({ type: "Assignment response", content: response });
          append(`✅ Assignment created successfully`);
        } catch (apiError) {
          let errorMessageToDisplay = "An unexpected API error occurred.";
          let statusCode = null;

          if (apiError.response) {
            const errorData = apiError.response.data;
            statusCode = apiError.response.status;

            if (errorData.error.includes("An open assignment already exists")) {
              setIsOpenAssignment(true);
            }

            if (errorData && typeof errorData.error === "string") {
              errorMessageToDisplay = errorData.error;
            } else if (apiError.message) {
              errorMessageToDisplay = `API Error ${statusCode}: ${apiError.message}`;
            } else {
              errorMessageToDisplay = `API responded with status ${statusCode} but no specific error message.`;
            }
          } else if (apiError.request) {
            errorMessageToDisplay =
              "API Network Error: No response received from server.";
          } else {
            errorMessageToDisplay = `Error during API request setup: ${
              apiError.message || "Unknown error"
            }`;
          }

          append(`❌ ${errorMessageToDisplay}`);
          showToast(errorMessageToDisplay, "error");
          endDebugging();
          append("Halt Debugging");
        }
      } catch (error) {
        append(`Error parsing YAML: ${error.message}`);
      }
    },
    [append, showToast],
  );

  const endDebugging = useCallback(() => {
    if (!isDebuggingRef.current) {
      append("No debugging session in progress");
      return;
    }
    isDebuggingRef.current = false;
    if (assignmentId) {
      terminateAssignment(assignmentId, "cancelled", "debug_ended");
    }
    setIsDebugging(false);

    if (removeListenerRef.current) {
      removeListenerRef.current();
      removeListenerRef.current = null;
    }
    debuggerService.disconnect();
    setProcessedCallbacks(new Set());
    setAssignmentId(null);
  }, [append, assignmentId]);

  useEffect(() => {
    const handleLaunchConfiguration = (e) => {
      if (isDebuggingRef.current) {
        append("Debugging session already in progress (use 'halt' to end)");
        return;
      }
      startDebugging(e.detail);
    };

    window.addEventListener("launchConfiguration", handleLaunchConfiguration);

    return () => {
      window.removeEventListener(
        "launchConfiguration",
        handleLaunchConfiguration,
      );
      if (removeListenerRef.current) {
        removeListenerRef.current();
      }
      debuggerService.disconnect();
    };
  }, [append, startDebugging]);

  useEffect(() => {
    window.debuggerState = {
      output,
      isDebugging,
      isOpenAssignment,
      processedCallbacks,
      append,
      startDebugging,
      endDebugging,
      assignmentId,
      clearOutput: () => setOutput([]),
    };
  }, [
    output,
    isDebugging,
    isOpenAssignment,
    processedCallbacks,
    append,
    startDebugging,
    endDebugging,
    assignmentId,
  ]);

  return null;
};

export default DebuggerStateManager;
